// Errors that a full page reload genuinely fixes — as opposed to app bugs,
// where reloading would just crash again (and loop forever if we auto-reload).
//
// Three classes qualify:
//  1. Hydration mismatches — server HTML differs from the client's first
//     render (relative timestamps, Date.now()-derived UI, extensions mutating
//     the DOM). A clean client-side reload renders consistently.
//  2. Stale chunk/module loads — the tab holds pre-deploy JS and asks for
//     chunk files that no longer exist (Jenkins swapped .next on deploy).
//  3. Stale deployment state — the tab talks to a newer server build (server
//     action ids rotated, RSC stream cut by a backend reload). Reload syncs
//     the tab to the current deployment.

const HYDRATION_PATTERNS = [
  /hydration failed/i,
  /error while hydrating/i,
  /text content does not match/i,
  /hydration completed but contains mismatches/i,
  // React 19 wording, so this keeps working after the next major upgrade.
  /server rendered html didn't match/i,
  // Minified React equivalents (react.dev/errors/NNN):
  // 418 hydration failed · 419 server couldn't finish Suspense boundary ·
  // 422/423 error while hydrating (inside/outside Suspense) · 425 text mismatch
  /minified react error #(418|419|422|423|425)/i,
];

const STALE_CHUNK_PATTERNS = [
  /chunkloaderror/i,
  /loading chunk [\w-]+ failed/i,
  /loading css chunk/i,
  /failed to fetch dynamically imported module/i, // Chrome dynamic import()
  /error loading dynamically imported module/i, // Firefox dynamic import()
  /importing a module script failed/i, // Safari dynamic import()
  /failed to load script/i, // next/script + route chunk preloads
];

const STALE_DEPLOY_PATTERNS = [
  // Server action id no longer exists on the server — classic stale tab after
  // a deploy ("Failed to find Server Action 'x'. This request might be from an
  // older or newer deployment").
  /failed to find server action/i,
  // RSC flight stream cut mid-request (gunicorn/pm2 restarted during render).
  /connection closed/i,
  // App-router internal state corrupted after a deploy swap.
  /invariant: attempted to hard navigate to the same url/i,
];

const errorText = (error: unknown): string => {
  if (!error) return "";
  if (typeof error === "string") return error;
  const err = error as { name?: unknown; message?: unknown; stack?: unknown };
  return [err.name, err.message, err.stack]
    .filter((part): part is string => typeof part === "string")
    .join(" | ");
};

// Webpack runtime crashing inside __webpack_require__ ("Cannot read properties
// of undefined (reading 'call')") is the signature of an HTML page referencing
// a chunk graph that no longer matches the deployed build. Only treat it as
// stale when webpack shows up in the same error — a plain app-level
// "reading 'call'" bug must NOT trigger a reload.
const isStaleWebpackRuntime = (text: string): boolean =>
  /reading '?call'?/i.test(text) && /webpack/i.test(text);

// A hydration error that still gets *reported* has already survived the
// auto-reload below, so it is reproducible rather than transient — the reporter
// uses this to attach the DOM state that explains why.
export const isHydrationError = (error: unknown): boolean => {
  const text = errorText(error);
  return !!text && HYDRATION_PATTERNS.some((pattern) => pattern.test(text));
};

export const isReloadRecoverableError = (error: unknown): boolean => {
  const text = errorText(error);
  if (!text) return false;
  return (
    [
      ...HYDRATION_PATTERNS,
      ...STALE_CHUNK_PATTERNS,
      ...STALE_DEPLOY_PATTERNS,
    ].some((pattern) => pattern.test(text)) || isStaleWebpackRuntime(text)
  );
};

// One auto-reload per path per window — if the same page errors again right
// after reloading, the error is NOT transient and the boundary must show its
// card instead (otherwise we'd reload in an endless loop).
const RELOAD_WINDOW_MS = 60_000;

const reloadKey = (): string =>
  `app-auto-reload:${window.location.pathname}`;

export const canAutoReload = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const last = Number(window.sessionStorage.getItem(reloadKey()) ?? 0);
    return Date.now() - last > RELOAD_WINDOW_MS;
  } catch {
    // sessionStorage unavailable (privacy mode) — don't risk a reload loop.
    return false;
  }
};

// Returns false when the reload could not be initiated (guard stamp failed to
// write — without it a loop is possible, so we refuse). Callers MUST fall back
// to their visible error UI in that case instead of waiting on a reload that
// will never come.
export const performAutoReload = (): boolean => {
  try {
    window.sessionStorage.setItem(reloadKey(), String(Date.now()));
  } catch {
    return false; // can't stamp the guard → don't reload
  }
  window.location.reload();
  return true;
};
